import { HttpClient, HttpContext, HttpContextToken, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { AcceptIncomingChat } from '../../interfaces/Chat/AcceptIncomingChat';
import { ConversationHistoryResponse } from '../../interfaces/Chat/ConversationHistoryResponse';
import { EndCustomerChat } from '../../interfaces/Chat/EndCustomerChat';
import { GetPendingRequestResponse } from '../../interfaces/Chat/GetPendingRequestResponse';
import { HandOverCustomerChat } from '../../interfaces/Chat/HandOverCustomerChat';
import { InitializeChat } from '../../interfaces/Chat/InitializeChat';
import { RejectIncomingChat } from '../../interfaces/Chat/RejectIncomingChat';
import { UpdateConversationNote } from '../../interfaces/Chat/UpdateConversationNote';
import { RatingResponse } from '../../interfaces/dashboard/RatingResponse';
import { GetTranscriptResponse } from '../../interfaces/transcript/GetTranscriptResponse';
import { InsertAgentActivity } from '../../interfaces/user/InsertAgentActivity';
import { MessageResponse } from '../transcript/MessageResponse';
import { Constants } from '@app/constants';
import { RestMessage } from '@app/core/types';
import { AuthenticationService, getContext } from '@app/core';
import { MongoStageBuilder } from '@projects/shared/src/app/services/filter';
import { momentCustomInstance as momentInst, toCamel } from '@projects/shared/src/app/utils/helper';
import {
  ConversationCloseBy,
  ConversationStatus,
  ConversationType,
  DeliveryReceiptsEnum,
  MessageType,
  ParticipantChannelType,
} from '../../interfaces/transcript/ConversationAndOfflineMessagesResponse';
import { FormattedEmail } from '@app/core/helpers';
import uniqBy from 'lodash/uniqBy';
import { TelephonyProvider } from '@projects/shared/src/app/providers/telephony/telephony.provider';
import { Conversation, Message } from '@twilio/conversations';
import { BehaviorSubject, Subject, Subscription, lastValueFrom, throttleTime } from 'rxjs';
import { ToastService } from '@projects/shared/src/app/services/toast/toast.service';
import { SignalrService } from '@app/core/signalr.service';
import { RealTimeEvents } from '@app/core/signalr.events';
import { FormBuilder, Validators } from '@angular/forms';
import { IPageInfo } from '@shared/modules/virtual-scroller/virtual-scroller';
import { CampaignService } from '@projects/shared/src/app/services/campaign.service';
@Injectable({
  providedIn: 'root',
})
export class ConversationService implements OnDestroy {
  defaultReqHeader = new HttpHeaders({ hideLoader: 'true' });

  connectionStatus: string = 'Disconnected';
  selectedChatIndex: number = -1;
  selectedChatId: string = '';
  selectedChatSid: string = '';
  conversations: any = {
    hasNextPage: true,
    isNextPageLoading: false,
    items: [],
    pageNo: 1,
    pageSize: 25,
    total: 0,
  };
  searchValue = '';
  sort = "{'last_message.date_created': -1}";
  requests: any = [];
  agents: any = [];
  userName = '';
  joinConversation: boolean = false;
  joinAgentConversation: boolean = false;
  subscriptions = new Array<Subscription>();
  currentTab = 1;
  settings: any;
  showSearch = false;
  showMenu = false;
  searchApplied = false;
  dispositionForm = this.fb.group({
    disposition: ['', [Validators.required]],
    note: [''],
  });
  dispositions = [];
  shareForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });
  isShareOpen: boolean = false;
  isMarkCompleteOpen = false;
  chatSettings: any = {};
  isViewRating: boolean = false;
  selectedConversationRating: any;
  conversationIdForRating: string = '';
  messageRequest: any;
  scrollSubscription: Subject<any> = new Subject();
  mentionItemsSubject = new BehaviorSubject([] as any[]);
  isCollapsed: boolean = false;
  userCampaignIds: any = [];
  searchingConversation: boolean = false;
  sharingConversation: boolean = false;
  selectedAgent: any = {
    index: null,
    name: '',
    id: '',
  };
  tab = 1;
  isTCPAOpen = false;
  tcpaMessage = 'By confirming, you authorize us to contact you at this number. Consent is not a condition of purchase';
  clearText$ = new Subject<void>();
  constructor(
    private httpClient: HttpClient,
    private authService: AuthenticationService,
    public telephony: TelephonyProvider,
    private toast: ToastService,
    private signalRService: SignalrService,
    private fb: FormBuilder,
    private campaignService: CampaignService
  ) {
    this.userCampaignIds = this.authService.getAllCampaigns().map((item: any) => item.Id);
    this.userName = FormattedEmail(this.authService.authData.Username.toLowerCase(), true);
    this.subscriptions.push(
      this.telephony.messaging.events.subscribe((event) => {
        switch (event.name) {
          case 'Conversation': {
            console.log(new Date().toLocaleString() + '  ➤  twilio conversation event', event.type);
            switch (event.type) {
              case 'joined':
                this.addUserConversation(event.data);
                break;
              case 'updated':
                break;
              case 'left':
                this.removeUserConversation(event.data);
                break;
              case 'typing':
                console.log(new Date().toLocaleString() + '  ➤  twilio conversation typing event', event.data);
                const { sid, typing } = event.data;
                const index = this.getIndex(sid);
                if (index > -1) {
                  this.conversations.items[index].messages.typing = typing;
                  // Handle customer inactivity and show him/her away
                  if (this.conversations.items[index].isOnlineTimeout !== null) {
                    clearTimeout(this.conversations.items[index].isOnlineTimeout);
                    this.conversations.items[index].isOnlineTimeout = null;
                  }
                  this.conversations.items[index].isOnline = true;
                  this.conversations.items[index].isOnlineTimeout = setTimeout(() => {
                    this.conversations.items[index].isOnline = false;
                    clearTimeout(this.conversations.items[index].isOnlineTimeout);
                    this.conversations.items[index].isOnlineTimeout = null;
                  }, Constants.CUSTOMER_INACTIVITY_TIME);
                }
                break;
              case 'online': {
                const { sid, online } = event.data;
                const index = this.getIndex(sid);
                if (index > -1) {
                  this.conversations.items[index].isOnline = online;
                }
                break;
              }
              case 'tcpa-consent': {
                const { sid, value } = event.data;
                const index = this.getIndex(sid);
                if (index > -1) {
                  if (!this.conversations.items[index].tcpa?.userResponse && value.userResponse) {
                    this.sendMessage(`Thank you. Your response has been recorded as ${value.userResponse}.`);
                    if (value.userResponse == 'accepted') {
                      this.toast.success("Customer accepted TCPA communication consent.");
                    }
                    else if (value.userResponse == 'rejected') {
                      this.toast.error("Customer rejected TCPA communication consent.");
                    }
                  }
                  this.conversations.items[index].tcpa = value;
                }
                break;
              }
            }
            break;
          }
          case 'Participant': {
            switch (event.type) {
              case 'left':
                const sid = event.data;
                const index = this.getIndex(sid);
                if (index > -1) {
                  this.conversations.items.splice(index, 1);
                  this.conversations.total = this.conversations.total - 1;
                  this.unselectConversation();
                }
                break;
            }
            break;
          }
          case 'Message': {
            switch (event.type) {
              case 'added':
                this.addMessage(event.data);
                break;
              case 'updated':
                this.editMessage(event.data);
                break;
            }
            break;
          }
          case 'Settings': {
            switch (event.type) {
              case 'get':
                this.settings = event.data;
                break;
            }
            break;
          }
          case 'Client': {
            switch (event.type) {
              case 'status':
                this.connectionStatus = event.data;
                break;
            }
            break;
          }
        }
      })
    );
    if (this.isChatPermission()) {
      this.subscriptions.push(
        this.signalRService.listen(RealTimeEvents.IncomingChatNotification).subscribe((data: any) => {
          if (this.userCampaignIds.indexOf(data.campaignId) < 0) return;

          console.log(`Signal event => IncomingChatNotification received with data`, data);
          this.currentTab = 1;
          const index = this.requests.findIndex((x: any) => x.customerConversationSid == data.customerConversationSid);
          if (index == -1) {
            data.showDetails = false;
            data.isOffline = false;
            this.requests.splice(0, 0, data);
          } else {
            this.requests[index].messageText = data.messageText;
            this.requests[index].messageDateTime = data.messageDateTime;
          }
          this.playRequestAudio();
          this.toast.info(`New visitor from ${data.channelType == 3 ? 'Messenger' : data.channelType == 4 ? 'Instagram' : data.customerInfo?.siteUrl || ''}`);
        })
      );
      this.subscriptions.push(
        this.signalRService.listen(RealTimeEvents.IncomingChatAcceptedNotification).subscribe((data: any) => {
          console.log(`Signal event => IncomingChatAcceptedNotification received with data`, data);
          const index = this.requests.findIndex((x: any) => x.customerConversationSid == data.customerConversationSid);
          if (index != -1) {
            this.requests.splice(index, 1);
          }
        })
      );
      this.subscriptions.push(
        this.signalRService.listen(RealTimeEvents.RatingNotification).subscribe((data: any) => {
          if (this.userCampaignIds.indexOf(data.campaignId) < 0) return;

          console.log(`Signal event => RatingNotification received with data`, data);
          const index = this.getIndex(data.conversationSid);
          if (index != -1) {
            this.toast.info(`Chat ${this.conversations.items[index].friendlyName} has been rated`);
            this.conversations.items[index].hasRated = true;
            this.conversations.items[index].rating = {
              Feedback: data.feedback,
              AvgRatingValue: data.avgRatingValue,
            };
          }
        })
      );
      this.subscriptions.push(
        this.signalRService.listen(RealTimeEvents.CustomerChatEndNotification).subscribe((data: any) => {
          if (this.userCampaignIds.indexOf(data.campaignId) < 0) return;

          console.log(`Signal event => CustomerChatEndNotification received with data`, data);
          const index = this.getIndex(data.customerConversationSid);
          if (index != -1) {
            this.toast.info(`Chat ${this.conversations.items[index].friendlyName} marked as closed`);
            this.conversations.items[index].hasDisposition = false;
            this.conversations.items[index].isClosed = true;
            this.conversations.items[index].closedByName = this.getClosedByName(ConversationCloseBy.Customer);
            this.conversations.items[index].closedBy = ConversationCloseBy.Customer;
            this.conversations.items[index].dateClosed = momentInst().format('MMMM Do YYYY, h:mm:ss a');
          }
        })
      );
      this.subscriptions.push(
        this.signalRService.listen(RealTimeEvents.MessageDelivered).subscribe((data: any) => {
          console.log(`Signal event => MessageDelivered received with data`, data);
          const { conversationId, time, type } = data;
          setTimeout(() => {
            const conv = this.conversations.items.find((x: any) => x.sid == conversationId);
            if (conv) {
              const d = DeliveryReceiptsEnum[type];
              let messages = conv.messages.items.filter(
                (x: any) => x.datetime > new Date(time) && !x.deliveryReceipt && x.incoming == false
              );
              if (messages.length == 0 && type == DeliveryReceiptsEnum.Read) {
                messages = conv.messages.items.filter(
                  (x: any) =>
                    x.datetime > new Date(time) &&
                    (x.deliveryReceipt?.delivered == 'all' || x.deliveryReceipt?.Delivered == 'all') &&
                    x.incoming == false
                );
              }
              if (messages.length > 0) {
                let res = this.formRealTimeDelivery(type);

                messages.forEach((x: any) => {
                  x.deliveryReceipt = res.delivery;
                  x.shortReceipt = res.signal;
                });
              }
            }
          }, 300);
        })
      );
      this.subscriptions.push(
        this.signalRService.listen(RealTimeEvents.HandoverChat).subscribe((data: any) => {
          console.log(`Signal event => HandoverChat received with data`, data);
          if (data.agentIdentity == this.userName) {
            this.toast.success('A chat has been transferred to you');
          }
        })
      );

      this.subscriptions.push(
        this.signalRService.listen(RealTimeEvents.AgentOnlineNotification).pipe(throttleTime(500)).subscribe((agentStatus: any) => {
          this.fetchOnlineAgents();
        })
      );

      this.subscriptions.push(
        this.signalRService.listen(RealTimeEvents.AgentOfflineNotification).pipe(throttleTime(500)).subscribe((agentStatus: any) => {
          this.fetchOnlineAgents();
        })
      );

      this.subscriptions.push(
        this.signalRService.listen(RealTimeEvents.CustomerChatEndNotification).subscribe((data: any) => {
          const index = this.requests.findIndex((x: any) => x.customerConversationSid == data.customerConversationSid);
          if (index != -1) {
            this.requests.splice(index, 1);
          }
        })
      );
    }
  }

  isChatPermission() {
    let user = this.authService.authData;
    const features = user.Features;
    const isAgent = features.find((x: any) => x.Name === 'agent_chat');
    return isAgent == undefined ? false : true;
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((s: Subscription) => s.unsubscribe());
  }

  async addUserConversation(conversation: Conversation) {
    const sid = `${conversation.sid}`;
    const uniqueName = `${this.userName}_${conversation.friendlyName}`;
    console.log(`Join event of conversation sid: ${sid}, Unique Name: ${uniqueName}`);
    let index: number;
    index = this.getIndex(sid);
    if (index < 0) {
      console.log(new Date().toLocaleString() + '  ➤  Not found in list using sid');
      console.log(new Date().toLocaleString() + '  ➤  Adding it');
      const dbConv: any = await lastValueFrom(this.getConversationBySid(sid));
      if (dbConv?.Id) {
        if (conversation._statusSource == 'sync' && dbConv.LastMessage?.Body) {
          this.playAudio();
          //show toast
        }
        const c = this.composeConversation(dbConv);
        if (this.joinConversation) {
          const unread_count = await conversation.getUnreadMessagesCount();
          if (unread_count == null) {
            const message_count = await conversation.getMessagesCount();
            if (message_count > 0) {
              c.unread_messages_count = message_count;
            }
          }
        }
        this.conversations.items.push(c);
        this.sortConversation(this.conversations.items);
        const cindex = this.getIndex(this.selectedChatSid ?? '');
        if (cindex > -1) {
          this.selectedChatIndex = cindex;
        }
      } else {
        console.log(`${sid} getConversationById not found`);
      }
    } else {
      console.log(new Date().toLocaleString() + '  ➤  found in list updating status');
      this.conversations.items[index].sid = sid;
      this.conversations.items[index].hasDisposition = false;
      this.conversations.items[index].status = ConversationStatus.Open;
      await this.setupUnreadMessages(conversation);
    }
    if (this.joinConversation) {
      setTimeout(() => {
        const idx = this.conversations.items.findIndex((x: any) => x.sid == sid);
        if (idx > -1) {
          this.selectConversation(this.conversations.items[idx], idx);
        }
        if (this.joinAgentConversation) {
          //set filter
          this.tab = 2;
          this.joinAgentConversation = false;
        } else {
          //set filter active
          this.tab = 1;
        }
        this.joinConversation = false;
      }, 1000);
    }
  }
  async removeUserConversation(sid: string) {
    const index = this.getIndex(sid);
    if (index > -1) {
      this.conversations.items[index].sid = sid;
      this.conversations.items[index].status = ConversationStatus.Close;
      this.conversations.items[index].isOnline = false;

      this.unselectConversation();
    }
  }
  async addMessage(message: Message) {
    const index = this.getIndex(message.conversation.sid);
    if (index > -1) {
      const conversationType = this.conversations.items[index].conversationType;
      const m = this.composeMessage(true, message, this.userName, conversationType);
      let count = 0;
      if (index != this.selectedChatIndex) {
        count = this.conversations.items[index].unread_messages_count + 1;
      }
      if (index == this.selectedChatIndex) {
        this.telephony.messaging.setAllMessagesRead(null, this.selectedChatSid);
      }
      if (m.direction == 'incoming') {
        //if(!selectChatSid || selectChatSid != message.conversation.sid) {
        this.playAudio();
        //dispatch(showNotification(m.body, m.username));
        this.toast.info('You have received a new message');
      }
      this.conversations.items[index].lastMessage = {
        body: m.body?.length > 22 ? m.body.substring(0, 22) + '..' : m.body,
        sid: m.sid,
        dateCreated: m.datetime.toISOString(),
      };
      this.conversations.items[index].unread_messages_count = count;
      this.conversations.items[index].messages.items.push(m);
      this.sortConversation(this.conversations.items);
      const cindex = this.getIndex(this.selectedChatSid);
      if (cindex > -1) {
        this.selectedChatIndex = cindex;
      }
      //if msg arrived and filter is not that of conv
      // if (conversationType == ConversationType.Agent && this.tab != 2) {
      //   this.tab = 2;
      // } else {
      //   this.tab = 1;
      // }
      //todo: this.telephony.messaging.events.next({ name: 'Message', type: 'scroll', data: this.conversations.items[index].messages.items.length -1 });
      this.scrollSubscription.next(this.conversations.items[index].messages.items.length - 1);
    }
  }

  typing() {
    const conversation = this.conversations.items[this.selectedChatIndex];
    if (conversation) {
      this.telephony.messaging.typing(null, conversation.sid)
    }
  }

  async editMessage(message: Message) {
    const ch_index = this.getIndex(message.conversation.sid);
    if (ch_index > -1) {
      const msg_index = this.conversations.items[ch_index].messages.items.findIndex((x: any) => x.sid == message.sid);
      if (msg_index > -1) {
        this.conversations.items[ch_index].messages.items[msg_index].attributes = message.attributes;
        this.conversations.items[ch_index].messages.items[msg_index].deliveryReceipt =
          message.aggregatedDeliveryReceipt;
        this.conversations.items[ch_index].messages.items[msg_index].shortReceipt = this.formatDelievery(
          message.aggregatedDeliveryReceipt,
          true
        );
        this.conversations.items[ch_index].messages.items[msg_index].lastMessageTime = message.dateUpdated?.toISOString;
      }
    }
  }

  getAgentConversations() {
    let filter = '';
    const stageBuilder = new MongoStageBuilder();
    stageBuilder.AddQueryString("'is_deleted': false, ");
    stageBuilder.AddIdClause("'current_assignee.user_id'", this.authService.authData.Id, '');
    if (this.searchValue) {
      const searchValue = this.searchValue.trim().replace(/^Chat#/, "");
      stageBuilder.AddOrQuery('friendly_name,nick_name', searchValue);
    }
    filter = stageBuilder.BuildString();
    const url = `${Constants.APIS.MESSENGER.GET_CONVERSATIONS}?PageNo=${this.conversations.pageNo}&PageSize=${this.conversations.pageSize}&Sort=${this.sort}&Filters=${filter}`;
    this.conversations.isNextPageLoading = true;

    this.httpClient
      .disableLoader()
      .get<RestMessage>(url, { context: getContext('messenger') })
      .subscribe((res) => {
        if (res.Error) {
          console.log(res.Message, res.ExceptionProvider);
          return;
        }
        const { Data, Total, HasNextPage } = res.Response;
        const data = Data.map(this.composeConversation);
        this.conversations.items = [...this.conversations.items, ...data];
        this.conversations.hasNextPage = HasNextPage;
        this.conversations.total = Total;
        this.conversations.isNextPageLoading = false;
        this.conversations.pageNo++;
        this.searchingConversation = false;
        if (!this.telephony.messaging.clientInitialized) {
          this.telephony.messaging.init();
          this.fetchOnlineAgents();
          this.fetchPendingRequests();
        }
      });
  }
  fetchChatList() {
    if (this.conversations.isNextPageLoading || !this.conversations.hasNextPage) return;
    this.getAgentConversations();
  }
  onMessageScroll(event: IPageInfo) {
    if (this.selectedChatIndex == -1 || !this.selectedChatSid) return;
    const messages = this.conversations.items[this.selectedChatIndex].messages;
    if (event.scrollStartPosition > 30 || messages.isNextPageLoading) return;
    if (messages.hasNextPage) {
      this.getMessages(messages.pageNo, messages.pageSize);
    }
  }
  getMessages(pageNo: number, pageSize: number) {
    let filter = '';
    const stageBuilder = new MongoStageBuilder();
    stageBuilder.AddQueryString("'is_deleted': false,");
    stageBuilder.AddQueryString("'type': 2,");
    stageBuilder.AddIdClause('conversation_id', this.selectedChatId, '');

    const sort = "{'created_on': -1}";

    filter = stageBuilder.BuildString();
    const url = `${Constants.APIS.MESSENGER.GET_MESSAGES}?PageNo=${pageNo}&PageSize=${pageSize}&Filters=${filter}&Sort=${sort}`;
    this.conversations.items[this.selectedChatIndex].messages.isNextPageLoading = true;
    this.messageRequest = this.httpClient
      .disableLoader()
      .get<RestMessage>(url, { context: getContext('messenger') })
      .subscribe((res) => {
        if (res.Error) {
          console.log(res.Message, res.ExceptionProvider);
          return;
        }
        const { Data, Total, HasNextPage } = res.Response;
        const chat_messages = this.conversations.items[this.selectedChatIndex].messages;
        const conversationType = this.conversations.items[this.selectedChatIndex].conversationType;
        let data = Data.reverse().map((x: any) => this.composeMessage(false, x, this.userName, conversationType));
        data = uniqBy([...data, ...chat_messages.items], 'sid');
        this.conversations.items[this.selectedChatIndex].messages.items = data;
        this.conversations.items[this.selectedChatIndex].messages.hasNextPage = HasNextPage;
        this.conversations.items[this.selectedChatIndex].messages.total = Total;
        this.conversations.items[this.selectedChatIndex].messages.isNextPageLoading = false;
        this.conversations.items[this.selectedChatIndex].messages.pageNo = pageNo + 1;
        this.conversations.items[this.selectedChatIndex].messages.pageSize = pageSize;
        this.telephony.messaging.setAllMessagesRead(null, this.selectedChatSid);
        if (pageNo == 1) {
          this.scrollSubscription.next(this.conversations.items[this.selectedChatIndex].messages.items.length - 1);
        }
      });
  }
  selectConversation(conv: any, index: number) {
    if (this.selectedChatSid == conv.sid) return;
    this.clearText();
    this.isMarkCompleteOpen = false;
    this.dispositionForm.reset();
    this.selectedChatId = conv.id;
    this.selectedChatSid = conv.sid;
    // Find conversation index by sid, index not correct due to scroll
    index = this.getIndex(conv.sid);

    this.selectedChatIndex = index;
    this.conversations.items[index].unread_messages_count = 0;
    const messages = this.conversations.items[index].messages;
    if (messages.hasNextPage) {
      if (this.messageRequest) {
        this.messageRequest.unsubscribe();
        this.messageRequest = null;
      }
      this.getMessages(messages.pageNo, messages.pageSize);
    } else {
      this.scrollSubscription.next(this.conversations.items[index].messages.items.length - 1);
    }
    this.getChatSettings(this.conversations.items[index].campaignId);
    this.fetchConversationHistory(this.selectedChatId);

  }
  searchConversation(reset: boolean) {
    if (this.searchingConversation)
      return;

    this.searchingConversation = true;
    if (reset) {
      this.searchValue = '';
      this.searchApplied = false;
    } else {
      this.searchApplied = true;
    }
    this.conversations.items = [];
    this.conversations.total = 0;
    this.conversations.pageNo = 1;
    this.selectedChatId = '';
    this.selectedChatSid = '';
    this.selectedChatIndex = -1;
    this.getAgentConversations();
  }
  async setupUnreadMessages(conversation: Conversation) {
    const index = this.getIndex(conversation.sid);
    if (index > -1) {
      if (this.selectedChatIndex !== index) {
        const unread_count = await conversation.getUnreadMessagesCount();
        if (unread_count == null) {
          const message_count = await conversation.getMessagesCount();
          if (message_count > 0) {
            this.conversations.items[index].unread_messages_count = message_count;
          }
        } else {
          this.conversations.items[index].unread_messages_count = unread_count;
        }
      }
    }
  }
  sendMessage(payload: any) {
    if (!this.selectedChatSid) return;
    const conv = this.conversations.items[this.selectedChatIndex];
    if (conv.status == ConversationStatus.Close) {
      console.log(new Date().toLocaleString() + '  ➤  Conv is closed');
    } else {
      console.log(new Date().toLocaleString() + '  ➤  Sending message');
      this.telephony.messaging.sendMessage(null, this.selectedChatSid, payload);
    }
  }
  getIndex(sid: string) {
    return this.conversations.items.findIndex((x: any) => x.sid == sid);
  }
  acceptChat(index: number, request: any) {
    const username = `${this.authService.authData.FirstName} ${this.authService.authData.LastName}`;
    const data = {
      CustomerConversationSid: request.customerConversationSid,
      AgentIdentity: this.userName,
      AgentInfo: {
        AgentName: username,
        Email: this.authService.authData.Email,
        FriendlyName: username,
        AvatarURL: this.authService.authData.ProfilePicUrl,
        IbexAgentId: this.authService.authData.Id,
      },
      OldParticipantSid: '',
      AccountSid: this.settings.AccountSid,
    };
    this.httpClient
      .post(Constants.APIS.MESSENGER.ACCEPT_CHAT, data, { context: getContext('messenger') })
      .subscribe({
        next: (data) => {
          this.joinConversation = true;
          this.toast.success('Chat accepted');
          this.requests.splice(index, 1);
        },
        error: (err) => {
          this.toast.info('Chat already accepted');
          this.requests.splice(index, 1);
        },
      });
  }
  setOption(tab: number) {
    this.currentTab = tab;
  }
  shareConversationFromSup(visitorId: string, conversationSid: string) {
    if (!this.shareForm.valid) {
      this.shareForm.markAsTouched();
      return;
    }

    if (this.sharingConversation) return;

    this.sharingConversation = true;
    const value = this.shareForm.value;
    const data = {
      VisitorId: visitorId,
      Email: value.email,
      CustomerConversationSid: conversationSid,
      IsSendCompleteHistory: true,
    };
    this.httpClient
      .post(Constants.APIS.MESSENGER.SHARE_CHAT, data, { context: getContext('messenger') })
      .subscribe((res) => {
        this.shareForm.reset();
        this.sharingConversation = false;
        this.toggleShare();
        this.toast.success('Chat shared successfully');
      });
  }
  shareConversation() {
    if (!this.shareForm.valid) {
      this.dispositionForm.markAsDirty();
      this.shareForm.markAllAsTouched();
      return;
    }
    if (this.sharingConversation) return;

    this.sharingConversation = true;
    const value = this.shareForm.value;
    const data = {
      VisitorId: this.conversations.items[this.selectedChatIndex].visitorId,
      Email: value.email,
      CustomerConversationSid: this.selectedChatSid,
      IsSendCompleteHistory: false,
    };
    this.httpClient
      .post(Constants.APIS.MESSENGER.SHARE_CHAT, data, { context: getContext('messenger') })
      .subscribe((res) => {
        this.shareForm.reset();
        this.toggleShare();
        this.sharingConversation = false;
        this.toast.success('Chat shared successfully');
      });
  }
  completeChat() {
    if (!this.dispositionForm.valid) {
      this.dispositionForm.markAsDirty();
      this.dispositionForm.markAllAsTouched();
      return;
    }
    this.clearText();
    const value = this.dispositionForm.value;
    const data = {
      AccountSid: this.settings.AccountSid,
      CustomerIdentity: this.userName,
      CustomerConversationSid: this.selectedChatSid,
      ParticipantSid: '',
      Disposition: value.disposition,
      Note: value.note,
    };
    const selectedChatIndex = this.selectedChatIndex;
    this.httpClient
      .post(Constants.APIS.MESSENGER.COMPLETE_CHAT, data, { context: getContext('messenger') })
      .subscribe((res) => {
        this.dispositionForm.reset();
        this.conversations.items[selectedChatIndex].hasDisposition = true;
        this.conversations.items[selectedChatIndex].disposition = value.disposition;
        this.conversations.items[selectedChatIndex].note = value.note;
        if (!this.conversations.items[selectedChatIndex].isClosed) {
          this.conversations.items[selectedChatIndex].isClosed = true;
          this.conversations.items[selectedChatIndex].closedByName = this.getClosedByName(
            ConversationCloseBy.Agent
          );
          // this.conversations.items[this.selectedChatIndex].closedByName = ConversationCloseBy.Agent.toString();
          this.conversations.items[selectedChatIndex].closedBy = ConversationCloseBy.Agent;
          this.conversations.items[selectedChatIndex].dateClosed = momentInst().format('MMMM Do YYYY, h:mm:ss a');
        }
        this.toggleComplete();
        this.toast.success('Chat mark as closed');
      });
  }
  getChatSettings(campaignId: string) {
    if (this.chatSettings[campaignId]) {
      this.conversations.items[this.selectedChatIndex].dispositions = this.chatSettings[campaignId].dispositions;
      this.conversations.items[this.selectedChatIndex].shortKeys = this.chatSettings[campaignId].shortKeys;
      this.updateMentionConfigItems();
      return;
    }
    const url = `${Constants.APIS.MESSENGER.GET_SETTINGS}/${campaignId}`;
    this.httpClient
      .disableLoader()
      .get(url, { context: getContext('messenger') })
      .subscribe((res: any) => {
        const dispositions = res.Dispositions.map((x: any) => ({ name: x }));
        const shortKeys = res.ChatShortKeys;
        this.conversations.items[this.selectedChatIndex].dispositions = dispositions;
        this.conversations.items[this.selectedChatIndex].shortKeys = shortKeys;
        this.chatSettings[campaignId] = {
          dispositions,
          shortKeys,
        };
        this.updateMentionConfigItems();
        this.tcpaMessage = res.TCPAConsentText;
      });
  }
  getChatRating() {
    if (!this.conversations.items[this.selectedChatIndex].hasRated) {
      return;
    }
    if (Object.keys(this.conversations.items[this.selectedChatIndex].rating).length > 0) {
      this.toggleRating(!this.isViewRating);
      return;
    }
    const url = `${Constants.APIS.MESSENGER.GET_RATING}?ConversationSid=${this.selectedChatSid}`;
    this.httpClient.get(url, { context: getContext('messenger') }).subscribe((res: any) => {
      this.toggleRating(true);
      this.conversations.items[this.selectedChatIndex].rating = res;
    });
  }

  hasPendingChats() {
    const userId = this.authService.getUserId();
    const url = `${Constants.APIS.MESSENGER.HAS_PENDING_REQUESTS}?userId=${userId}`;
    return this.httpClient.get(url, { context: getContext('messenger'), headers: this.defaultReqHeader });
  }

  getChatRatingBySid(conversationSid: string) {
    if (this.selectedConversationRating && this.conversationIdForRating == conversationSid) {
      this.toggleRating(!this.isViewRating);
      return;
    }
    this.conversationIdForRating = conversationSid;
    const url = `${Constants.APIS.MESSENGER.GET_RATING}?ConversationSid=${conversationSid}`;
    this.httpClient
      .get(url, { context: getContext('messenger'), headers: this.defaultReqHeader })
      .subscribe((res: any) => {
        this.toggleRating(true);
        this.selectedConversationRating = res;
      });
  }
  getChatHistory(conversationId: string) {
    const url = `/conversation/getConversationHistoryById/${conversationId}`;
    return this.httpClient.get<Array<ConversationHistoryResponse>>(url, { context: getContext('messenger') });
  }
  getConversationBySid(conversationSid: string) {
    const url = `${Constants.APIS.MESSENGER.GET_CONVERSATION_BY_SID}/${conversationSid}`;
    return this.httpClient.disableLoader().get<GetTranscriptResponse>(url, { context: getContext('messenger') });
  }

  composeConversation = (conv: any) => {
    return {
      id: conv.Id,
      sid: conv.CurrentConversationSid,
      flecx_id: conv.FlecxId,
      friendlyName: conv.FriendlyName,
      nickName: conv.NickName || conv.FriendlyName,
      uniqueName: conv.UniqueName,
      isOnline: false,
      tcpa: conv.TCPA,
      unread_messages_count: 0,
      lastMessage: {
        body:
          conv.LastMessage != null
            ? conv.LastMessage.Body?.length > 22
              ? conv.LastMessage.Body.substring(0, 22) + '..'
              : conv.LastMessage.Body
            : '',
        dateCreated: conv.LastMessage != null ? conv.LastMessage.DateCreated : '',
        messageSid: conv.LastMessage != null ? conv.LastMessage.MessageSid : '',
      },
      status: conv.CurrentStatus,
      isClosed: conv.CurrentStatus == ConversationStatus.Close ? true : false,
      messages: {
        hasNextPage: true,
        isNextPageLoading: false,
        pageNo: 1,
        pageSize: 20,
        items: [],
        total: 0,
        typing: {
          on: false,
          user: '',
        },
      },
      history: {
        items: [],
      },
      channelType: conv.Site.EntryType,
      hasRated: conv.HasRated,
      obtainedRating: conv.Rating,
      closedBy: conv.ClosedBy,
      closedByName: conv.ClosedBy != null ? this.getClosedByName(conv.ClosedBy) : '',
      dateClosed: momentInst(conv.DateClosed).format('MMMM Do YYYY, h:mm:ss a'),
      note: conv.Note,
      disposition: conv.Disposition,
      visitorId: conv.VisitorId,
      customerInfo: {
        BTN: conv.CustomerInfo != null ? conv.CustomerInfo.BTN : '',
        SiteUrl: conv.Site != null ? conv.Site.Url : '',
        IP: conv.CustomerInfo != null && conv.CustomerInfo.Address != null ? conv.CustomerInfo.Address.IP : '',
        Name: conv.CustomerInfo != null ? conv.CustomerInfo.Name : '',
        TFN: conv.Site != null ? conv.Site.TFN : '',
        Email: conv.CustomerInfo != null ? conv.CustomerInfo.Email : '',
        Zip: conv.CustomerInfo != null && conv.CustomerInfo.Address != null ? conv.CustomerInfo.Address.Zip : '',
        City: conv.CustomerInfo != null && conv.CustomerInfo.Address != null ? conv.CustomerInfo.Address.City : '',
        State: conv.CustomerInfo != null && conv.CustomerInfo.Address != null ? conv.CustomerInfo.Address.State : '',
        Country:
          conv.CustomerInfo != null && conv.CustomerInfo.Address != null ? conv.CustomerInfo.Address.Country : '',
        Referrer: conv.CustomerInfo != null ? conv.CustomerInfo.Referrer : '',
        ProfilePicUrl: conv.CustomerInfo != null ? conv.CustomerInfo.ProfilePicUrl : '',
      },
      hasDisposition: conv.Disposition == null ? false : true,
      conversationType: conv.ConversationType,
      campaignId: conv.CampaignId,
      rating: {},
      icon:
        conv.Site.EntryType == ParticipantChannelType.Web
          ? 'fa-light fa-globe social-icon'
          : conv.Site.EntryType == ParticipantChannelType.Messenger
            ? 'fa-brands fa-facebook-messenger social-icon social-icon--fb'
            : conv.Site.EntryType == ParticipantChannelType.WhatsApp
              ? 'fa-brands fa-whatsapp social-icon social-icon--wa'
              : conv.Site.EntryType == ParticipantChannelType.Instagram
                ? 'fa-brands fa-instagram social-icon social-icon--fb'
                : 'fa-regular fa-comment-sms social-icon social-icon--wa',
    };
  };

  composeMessage(fromTwilio: boolean, message: any, userName: string, conversationType: number) {
    let direction = '';
    if (fromTwilio) {
      if (conversationType == ConversationType.Agent) {
        if (userName == message.author) {
          direction = 'outgoing';
        } else {
          direction = FormattedEmail(message.author.toLowerCase(), true) == userName ? 'outgoing' : 'incoming';
        }
      } else {
        direction = FormattedEmail(message.author.toLowerCase(), true) == userName ? 'outgoing' : 'incoming';
      }
      const name = `${this.authService.authData.FirstName} ${this.authService.authData.LastName}`;
      return {
        sid: message.sid,
        username: `${this.authService.authData.FirstName} ${this.authService.authData.LastName} `,
        prefix: name
          .split(' ')
          .map((item: any) => item.toUpperCase().substring(0, 1))
          .join(' '),
        date: momentInst(message.dateUpdated).calendar(),
        body: message.body,
        index: message.index,
        incoming: direction == 'incoming' ? true : false,
        direction,
        deliveryReceipt: message.deliveryReceipt,
        shortReceipt: this.formatDelievery(message.deliveryReceipt, fromTwilio),
        datetime: new Date(message.dateUpdated),
        media: message.medias ? message.medias.map((x: any) => x.state) : null,
      };
    } else {
      if (conversationType == ConversationType.Agent) {
        if (message.Sender.UserId == this.authService.authData.Id) {
          direction = 'outgoing';
        } else {
          direction = 'incoming';
        }
      } else {
        if (message.Sender.Type == MessageType.Customer && message.Body.includes("We're not around")) {
          direction = 'outgoing';
        } else {
          direction = message.Sender.Type == MessageType.Customer ? 'incoming' : 'outgoing';
        }
      }
    }
    const name = `${this.authService.authData.FirstName} ${this.authService.authData.LastName}`;
    return {
      sid: message.Sid,
      username: message.Sender.Name,
      prefix: name
        .split(' ')
        .map((item: any) => item.toUpperCase().substring(0, 1))
        .join(' '),
      date: momentInst(message.ModifiedOn).calendar(),
      body: message.Body,
      index: message.Index,
      deliveryReceipt: message.DeliveryReceipt,
      shortReceipt: this.formatDelievery(message.DeliveryReceipt, fromTwilio),
      datetime: new Date(message.ModifiedOn),
      incoming: direction == 'incoming' ? true : false,
      media: message.Media,
      direction,
    };
  }
  sortConversation(items: any) {
    items.sort((a: any, b: any) => {
      if (!a.lastMessage) {
        return 1;
      } else if (!a.lastMessage.dateCreated) {
        return 1;
      }
      if (!b.lastMessage) {
        return -1;
      } else if (!b.lastMessage.dateCreated) {
        return -1;
      }
      const dateA = new Date(a.lastMessage.dateCreated);
      const dateB = new Date(b.lastMessage.dateCreated);
      return dateA < dateB ? 1 : -1;
    });
  }
  toggleSearch() {
    this.showSearch = !this.showSearch;
  }
  toggleMenu() {
    this.showMenu = !this.showMenu;
  }
  unselectConversation() {
    this.selectedChatId = '';
    this.selectedChatSid = '';
    this.selectedChatIndex = -1;
  }
  toggleShare() {
    this.shareForm.reset();
    this.isShareOpen = !this.isShareOpen;
    this.showMenu = false;
    this.isMarkCompleteOpen = false;
    this.isViewRating = false;
    this.isTCPAOpen = false;
  }
  toggleComplete() {
    this.isMarkCompleteOpen = !this.isMarkCompleteOpen;
    this.showMenu = false;
    this.isShareOpen = false;
    this.isTCPAOpen = false;
  }
  toggleTCPA() {
    this.isTCPAOpen = !this.isTCPAOpen;
    this.showMenu = false;
    this.isMarkCompleteOpen = false;
    this.isViewRating = false;
    this.isShareOpen = false;
  }
  closeProfileMenu(event: any) {
    this.showMenu = false;
  }
  getClosedByName(type: number) {
    if (type == ConversationCloseBy.Agent) {
      return 'Agent';
    } else if (type == ConversationCloseBy.Customer) {
      return 'Customer';
    }
    return 'System';
  }
  toggleRating(value: boolean) {
    this.isViewRating = value;
    this.isShareOpen = false;
  }
  formatDelievery(deliveryReceipt: any, isTwilio: boolean) {
    let signal = { class: '', text: '' };
    if (isTwilio) {
      if (!deliveryReceipt) return signal;

      if (deliveryReceipt.read === 'all') {
        signal.text = 'Read';
        signal.class = 'fa-check-double';
      } else if (deliveryReceipt.delivered === 'all') {
        signal.text = 'Delivered';
        signal.class = 'fa-check-double';
      } else if (deliveryReceipt.undelivered === 'all') {
        signal.text = 'Failed';
        signal.class = 'fa-times';
      } else if (
        deliveryReceipt?.sent === 'all' &&
        deliveryReceipt?.delivered === 'none' &&
        deliveryReceipt?.undelivered === 'none'
      ) {
        signal.text = 'Sent';
        signal.class = 'fa-check';
      }
      return signal;
    } else {
      if (!deliveryReceipt) return signal;
      if (deliveryReceipt.Read === 'all') {
        signal.text = 'Read';
        signal.class = 'fa-check-double';
      } else if (deliveryReceipt.Delivered === 'all') {
        signal.text = 'Delivered';
        signal.class = 'fa-check-double';
      } else if (deliveryReceipt.UnDelivered === 'all') {
        signal.text = 'Failed';
        signal.class = 'fa-times';
      } else if (
        deliveryReceipt?.Sent === 'all' &&
        deliveryReceipt?.Delivered === 'none' &&
        deliveryReceipt?.UnDelivered === 'none'
      ) {
        signal.text = 'Sent';
        signal.class = 'fa-check';
      }
      return signal;
    }
  }
  formRealTimeDelivery(deliveryReceipt: DeliveryReceiptsEnum) {
    let signal = { class: '', text: '' };
    let delivery = {
      delivered: 'none',
      failed: 'none',
      read: 'none',
      sent: 'none',
      undelivered: 'none',
    };
    if (deliveryReceipt === DeliveryReceiptsEnum.Read) {
      signal.text = 'Read';
      signal.class = 'fa-check-double';
      delivery.read = 'all';
      delivery.delivered = 'all';
      delivery.sent = 'all';
    } else if (deliveryReceipt === DeliveryReceiptsEnum.Delivered) {
      signal.text = 'Delivered';
      signal.class = 'fa-check-double';
      delivery.delivered = 'all';
      delivery.sent = 'all';
    } else if (deliveryReceipt === DeliveryReceiptsEnum.Undelivered) {
      signal.text = 'Failed';
      signal.class = 'fa-times';
      delivery.undelivered = 'all';
    } else if (deliveryReceipt === DeliveryReceiptsEnum.Sent) {
      signal.text = 'Sent';
      signal.class = 'fa-check';
      delivery.sent = 'all';
    }
    return { signal, delivery };
  }
  fetchOnlineAgents() {
    const url = `${Constants.APIS.MESSENGER.FETCH_ONLINE_CHAT_AGENTS}`;
    const data = {};
    this.httpClient
      .disableLoader()
      .post(url, data)
      .subscribe((res: any) => {
        this.agents = res.map((agent: any) => ({ ...agent, FullName: agent.FirstName + ' ' + agent.LastName }));
      });
  }
  async transferChat(agent: any) {
    if (!this.selectedChatSid) {
      this.toast.error('Please select conversation to transfer');
      return;
    }
    const index = this.getIndex(this.selectedChatSid);
    if (this.conversations.items[index].conversationType == ConversationType.Agent) {
      this.toast.error('Cannot transfer agent conversation');
      return;
    }
    const username = `${agent.FirstName} ${agent.LastName}`;
    const data = {
      CustomerConversationSid: this.selectedChatSid,
      AgentIdentity: FormattedEmail(agent.Email.toLowerCase(), true),
      AgentInfo: {
        AgentName: username,
        Email: agent.Email,
        FriendlyName: username,
        AvatarURL: agent.ProfilePicUrl,
        IbexAgentId: agent.Id,
      },
      OldParticipantSid: '',
      AccountSid: this.settings.AccountSid,
    };
    if (this.conversations.items[this.selectedChatIndex].isClosed) {
      this.toast.error('Conversation is in closed state, cannot transfer');
      return;
    }
    const participant = await this.telephony.messaging.getParticipantByIdentity(null, this.selectedChatSid);
    if (!participant) {
      this.toast.error('Participant not found');
      return;
    }
    data.OldParticipantSid = participant.sid;
    // console.log(data);
    this.httpClient
      .post(Constants.APIS.MESSENGER.HANDOVER_CHAT, data, { context: getContext('messenger') })
      .subscribe((res: any) => {
        this.toast.success('Chat transfered successfully');
      });
  }
  fetchConversationHistory(id: string) {
    const url = `${Constants.APIS.MESSENGER.CONVERSATION_HISTORY}/${id}`;
    this.httpClient
      .disableLoader()
      .get(url, { context: getContext('messenger') })
      .subscribe((res: any) => {
        this.conversations.items[this.selectedChatIndex].history.items = res.filter(
          (x: any) => x.ConversationSid != this.selectedChatSid
        );
      });
  }

  updateMentionConfigItems() {
    let items: any[] = [];
    const shortKeys = this.conversations.items[this.selectedChatIndex]?.shortKeys;

    if (shortKeys && Object.keys(shortKeys).length > 0) {
      items = Object.keys(shortKeys).map((key) => ({ label: key, value: shortKeys[key] }));
    }

    this.mentionItemsSubject.next(items);
  }

  toggleCollapsed() {
    this.isCollapsed = !this.isCollapsed;
  }

  fetchPendingRequests() {
    const ids = this.authService.authData.Campaigns.map((x: any) => x.Id);
    const url = `${Constants.APIS.MESSENGER.PENDING_REQUESTS}`;
    this.httpClient
      .disableLoader()
      .post(url, { CampaignIds: ids }, { context: getContext('messenger') })
      .subscribe((requests: any) => {
        let list: any = [];
        requests.forEach((x: any) => {
          x.showDetails = false;
          x.isOffline = true;
          list.push(toCamel(x));
        });
        this.requests = [...list, ...this.requests];
        if (list.length > 0) {
          this.toast.info(`You have ${list.length} offline message requests`);
        }
      });
  }
  toggleAgentTransfer(agent: any) {
    if (!agent) {
      this.selectedAgent.id = "";
      return;
    }
    let index = this.agents.findIndex((x: any) => x.Id == agent.Id);
    if (index > -1) {
      this.selectedAgent.index = index;
      this.selectedAgent.id = this.agents[index].Id;
      this.selectedAgent.name = `${this.authService.getUserFullName()}_${this.agents[index].FullName}`;
    }
  }
  hideAgentTransferModal() {
    this.selectedAgent.index = null;
    this.selectedAgent.id = '';
    this.selectedAgent.name = '';
  }

  createAgentConversation() {
    let user = this.authService.authData;
    const payload = {
      CampaignId: this.campaignService.primaryCampaign.Id,
      CreatorParticipantId: user.Id,
      ParticipantIds: [user.Id, this.selectedAgent.id],
      ConversationName: this.selectedAgent.name,
      AccountSid: this.settings.AccountSid,
    };
    this.joinAgentConversation = true;
    this.joinConversation = true;
    this.httpClient
      .post(Constants.APIS.MESSENGER.CREATE_AGENT_CONVERSATION, payload, { context: getContext('messenger') })
      .subscribe((requests: any) => {
        this.toast.success('Conversation created');
        this.hideAgentTransferModal();
      });
  }
  setTab(tab: number) {
    this.tab = tab;
  }
  playAudio() {
    try {
      const audio = new Audio('/assets/audio/sms.mp3');
      audio.play();
    } catch (e) { }
  }
  playRequestAudio() {
    try {
      const audio = new Audio('/assets/audio/request.mp3');
      audio.play();
    } catch (e) { }
  }
  async sendTCPAMessage() {
    const conv = this.conversations.items[this.selectedChatIndex];
    switch (conv.channelType) {
      case ParticipantChannelType.Instagram:
      case ParticipantChannelType.Messenger:
        if (conv.tcpa?.userResponse) {
          this.toast.info('TCPA response receive for this conversation. The response is ' + conv.tcpa.userResponse);
          return;
        }
        const result = await this.telephony.messaging.sendTCPAMessage(null, this.selectedChatSid, this.tcpaMessage);

        if (!result) {
          this.toast.info('TCPA message already sent for this conversation');
          return;
        }

        const url = `${Constants.APIS.MESSENGER.SEND_FB_QUICK_REPLY}`;
        this.httpClient
          .post(url, { ConversationSid: this.selectedChatSid }, { context: getContext('messenger') })
          .subscribe((requests: any) => {
          });
        break;
      case ParticipantChannelType.Web:
        if (conv.tcpa?.userResponse) {
          this.toast.info('TCPA response receive for this conversation. The response is ' + conv.tcpa.userResponse);
          return;
        }
        this.telephony.messaging.sendTCPAMessage(null, this.selectedChatSid, this.tcpaMessage);
        break;
    }
    this.toggleTCPA();
  }
  public clearText():void {
    this.clearText$.next();
  }
}
